import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { HelpCircle } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

// NOTE: keep these questions and answers in sync with the FAQPage JSON-LD in
// public/index.html. Google and AI answer engines expect the on-page text to
// match the structured data. Answers are always rendered (no collapse) so the
// no-JS prerender snapshot and crawlers see the full content.
const faqs = [
  {
    q: "What is a Smart Digital Doorway?",
    a: "A Smart Digital Doorway is not a brochure website. It is a working system that answers customers, captures leads, books appointments, follows up, and builds trust in the background, so your online presence does real work while you run your business.",
  },
  {
    q: "What areas does White Beard Solutions serve?",
    a: "White Beard Solutions serves local business owners in Kingsville, Ontario and the Windsor-Essex Region. Work is done remotely and on-site as needed, so nearby towns across Essex County are welcome too.",
  },
  {
    q: "Who is behind White Beard Solutions?",
    a: "White Beard Solutions is the personal brand of Paul Hughes, a Master Business Craftsman with more than 40 years of senior technology experience. It operates through Doorways Into Your Business (DIYB).",
  },
  {
    q: "How is this different from hiring a web designer?",
    a: "A web designer usually hands you a website and walks away. White Beard Solutions installs systems that keep working after launch, such as review management, booking, missed-call recovery, and AI answering, chosen for your business and built to last.",
  },
  {
    q: "What kinds of businesses do you work with?",
    a: "Local and small businesses that rely on calls, bookings, and repeat customers, such as trades, clinics, shops, and service providers across Kingsville and the Windsor-Essex Region.",
  },
  {
    q: "How do I get started?",
    a: "Start with a short conversation about your business. From there you get a clear recommendation and a fixed plan to install the right system. Reach out through the contact page at diyb.ca or call (519) 930-1758.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-36 bg-[#0a192f]"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
        >
          Questions, Answered
        </motion.p>

        <motion.h2
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-4"
        >
          Common <span className="text-gold-gradient">Questions</span>
        </motion.h2>

        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-body text-fluid-body text-slate-400 max-w-2xl mb-4"
        >
          Straight answers about how a Smart Digital Doorway works and who it is for.
        </motion.p>

        <Separator className="bg-gold/20 mb-12 max-w-xs" />

        {/* FAQ Grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {faqs.map((item, i) => (
            <motion.div
              key={item.q}
              data-testid={`faq-item-${i}`}
              className="card-glow relative bg-[#0f172a] border border-slate-800 rounded-sm p-7 hover:border-gold/30 transition-all duration-400"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
            >
              <div className="flex items-start gap-3 mb-3">
                <HelpCircle size={20} className="text-gold flex-none mt-0.5" />
                <h3 className="font-heading font-bold text-fluid-h3 text-slate-50 uppercase tracking-wide">
                  {item.q}
                </h3>
              </div>
              <p className="font-body text-slate-400 text-fluid-body">{item.a}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
